import React, { createContext, useState, useContext, useEffect } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { userData } from "../../store";
import { navbarNotificationsAtom } from "../../store/navigation";
import config from "../../config";
import {
  emitSession401,
  emitUserBlocked,
} from "../../services/sessionEvents";

const WebSocketContext = createContext(null);

export const useWebSocket = () => useContext(WebSocketContext);

export const WebSocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [receivedMessages, setReceivedMessages] = useState([]);
  const userInfoVal = useRecoilValue(userData);
  const setNavbarNotifications = useSetRecoilState(navbarNotificationsAtom);

  useEffect(() => {
    // This effect handles the WebSocket connection lifecycle.
    const userId = userInfoVal ? userInfoVal.ID : null;
    if (userId && !socket) {
      if (!userId) {
        console.error("WebSocket: User ID not found.");
        return;
      }

      // Check if WebSocket URL is configured
      const webSocketUrl = config.apiGateway.WEB_SOCKET_URL;
      if (
        !webSocketUrl ||
        webSocketUrl.includes("placeholder") ||
        webSocketUrl.includes("your-websocket")
      ) {
        console.error(
          "WebSocket: URL not configured. Set VITE_REACT_APP_WEB_SOCKET_URL in .env file",
        );
        return;
      }

      // Per your request, let's create a concatenated and encoded user ID.
      const concatenatedUserId = `Client#${userId}`;
      const encodedUserId = encodeURIComponent(concatenatedUserId);
      const ws = new WebSocket(`${webSocketUrl}?user_id=${encodedUserId}`);
      console.log("WebSocket: Attempting to connect to", webSocketUrl);

      ws.onopen = () => {
        console.log("WebSocket: Connected");
      };

      ws.onmessage = (event) => {
        try {
          const messageData = JSON.parse(event.data);
          const rawResponse =
            typeof messageData.response === "string"
              ? JSON.parse(messageData.response)
              : messageData.response || {};
          const innerResponse = rawResponse?.response || rawResponse;

          const notificationType = String(
            messageData.notificationType || "",
          ).toLowerCase();
          const eventType = String(
            innerResponse.EventType || innerResponse.eventType || "",
          ).toUpperCase();
          const isBlockedUserEvent =
            innerResponse.IsBlocked === true || innerResponse.isBlocked === true;

          if (notificationType === "sessionevent" && isBlockedUserEvent) {
            emitUserBlocked({
              message:
                innerResponse.Message ||
                "Your account has been blocked by an administrator.",
            });
            // Keep blocked events silent in the floating notification UI.
            return;
          }

          if (
            notificationType === "sessionevent" &&
            eventType === "SESSION_TERMINATED"
          ) {
            emitSession401();
            // Keep session lifecycle events silent in the floating notification UI.
            return;
          }

          const newMessage = {
            id: new Date().getTime(),
            pk: innerResponse.PK,
            name: innerResponse.Name,
            heading: messageData.notificationType || "New Notification",
            content: innerResponse.Heading || "You have a new notification.",
            timestamp: new Date().toLocaleString(),
          };
          //console.log(newMessage.pk);

          setReceivedMessages((prev) => [newMessage, ...prev]);

          // Also push into the shared navbar notifications atom so the
          // sidebar sheet and notifications table reflect the new item
          // immediately without requiring a re-fetch.
          const listItem = {
            pk: innerResponse.PK,
            name: innerResponse.Name,
            sentTo: innerResponse.SentTo ?? innerResponse.sentTo ?? "",
            heading: innerResponse.Heading || "New Notification",
            message:
              innerResponse.Message ??
              innerResponse.message ??
              innerResponse.Body ??
              "",
            notificationType:
              messageData.notificationType ??
              innerResponse.NotificationType ??
              "",
            CreatedAt:
              innerResponse.CreatedAt ??
              innerResponse.createdAt ??
              new Date().toISOString(),
            isRead: false,
          };

          setNavbarNotifications((prev) => {
            const existing = Array.isArray(prev) ? prev : [];
            if (listItem.pk && existing.some((n) => n.pk === listItem.pk)) {
              return existing;
            }
            return [listItem, ...existing];
          });
        } catch (error) {
          console.error("Error parsing WebSocket message:", error);
          setReceivedMessages((prev) => [
            {
              id: new Date().getTime(),
              heading: "New Notification",
              content: event.data, // raw fallback
              timestamp: new Date().toLocaleString(),
            },
            ...prev,
          ]);
        }
      };

      ws.onclose = () => {
        console.log("WebSocket: Disconnected");
        setSocket(null);
      };

      ws.onerror = (error) => {
        console.error(
          "WebSocket: Connection error. Ensure the WebSocket server is running and URL is correct.",
          error,
        );
      };

      setSocket(ws);

      // Cleanup function to close the socket when the component unmounts
      // or when the user logs out (userInfoVal becomes empty).
      return () => {
        console.log("WebSocket: Cleaning up and disconnecting.");
        ws.close();
        setSocket(null);
      };
    }
  }, [userInfoVal, socket]); // Dependencies for the effect

  const disconnectWebSocket = () => {
    if (socket) {
      socket.close();
    }
  };

  const removeMessage = (id) => {
    setReceivedMessages((prev) => prev.filter((msg) => msg.id !== id));
  };

  const value = {
    socket,
    disconnectWebSocket,
    receivedMessages,
    removeMessage,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};
